/**
 * Текст уведомления в интерфейсе (§18.4) — общий для сайта и приложения.
 *
 * Состав уведомления приходит с сервера полями, а не готовой строкой:
 * строка, собранная на сервере, не даёт выделить «было → стало» другим
 * начертанием, подставить ссылку на комментарий и сократить выдержку.
 *
 * Формулировки здесь и в серверном `render.py` намеренно разные: письмо
 * обязано быть самодостаточным, а строка ленты стоит рядом с названием
 * задачи и повторять его не должна.
 */

import { NotificationCategory } from "@taskflow/contracts";

import type { NotificationItem } from "./api";

/** Подписи категорий. Тот же список — в фильтре истории и в профиле. */
export const CATEGORY_LABELS: Record<NotificationCategory, string> = {
  [NotificationCategory.ASSIGNED]: "Назначение исполнителем",
  [NotificationCategory.UNASSIGNED]: "Снятие с задачи",
  [NotificationCategory.MENTIONED]: "Упоминание",
  [NotificationCategory.COMMENT]: "Комментарии",
  [NotificationCategory.OVERDUE]: "Просроченный срок",
  [NotificationCategory.TASK_REMINDER]: "Напоминание по задаче",
  [NotificationCategory.STATUS_CHANGED]: "Изменение статуса",
  [NotificationCategory.FIELDS_CHANGED]: "Изменение полей задачи",
  [NotificationCategory.ATTACHMENT]: "Вложения",
  [NotificationCategory.SUBTASK]: "Подзадачи",
  [NotificationCategory.ARCHIVED]: "Задача закрыта",
  [NotificationCategory.RESTORED]: "Задача восстановлена",
  [NotificationCategory.BOARD_INVITE]: "Доступ к доске",
  [NotificationCategory.NOTE_INVITE]: "Приглашение в заметку",
  [NotificationCategory.NOTE_REMINDER]: "Напоминание",
  [NotificationCategory.VACATION_UPCOMING]: "Скоро отпуск",
  [NotificationCategory.VACATION_CONFLICT]: "Пересечение отпусков",
  [NotificationCategory.VACATION_CHANGED]: "Изменение отпуска",
  [NotificationCategory.TASK_CREATED]: "Новая задача",
  [NotificationCategory.TASK_DELETED]: "Задача удалена",
};

/**
 * Подписи переключателей.
 *
 * От первого лица («меня назначили»), а не от третьего: человек
 * настраивает, о чём тревожить ЕГО, и формулировка обязана это называть.
 * В ленте уведомлений подписи другие — там строка описывает событие,
 * а не настройку (CATEGORY_LABELS).
 */
export const PREF_LABELS: Record<NotificationCategory, string> = {
  [NotificationCategory.ASSIGNED]: "Меня назначили исполнителем",
  [NotificationCategory.UNASSIGNED]: "Меня сняли с задачи",
  [NotificationCategory.MENTIONED]: "Меня упомянули",
  [NotificationCategory.COMMENT]: "Новый комментарий в моей задаче",
  [NotificationCategory.OVERDUE]: "Срок истёк",
  [NotificationCategory.TASK_REMINDER]: "Напоминание по задаче",
  [NotificationCategory.STATUS_CHANGED]: "Изменился статус задачи",
  [NotificationCategory.FIELDS_CHANGED]: "Изменились поля задачи: срок, приоритет, описание",
  [NotificationCategory.ATTACHMENT]: "К задаче прикрепили файл",
  [NotificationCategory.SUBTASK]: "Появилась или выполнена подзадача",
  [NotificationCategory.ARCHIVED]: "Задачу закрыли",
  [NotificationCategory.RESTORED]: "Задачу восстановили",
  [NotificationCategory.BOARD_INVITE]: "Приглашение на доску",
  [NotificationCategory.NOTE_INVITE]: "Меня пригласили в заметку",
  [NotificationCategory.NOTE_REMINDER]: "Напоминание из заметки",
  [NotificationCategory.VACATION_UPCOMING]: "За неделю до моего отпуска",
  [NotificationCategory.VACATION_CONFLICT]: "Мой отпуск пересёкся с отпуском коллеги",
  [NotificationCategory.VACATION_CHANGED]: "Мой отпуск изменил другой человек",
  [NotificationCategory.TASK_CREATED]: "Новая задача там, где я наблюдаю",
  [NotificationCategory.TASK_DELETED]: "Удалена задача там, где я наблюдаю",
};

/**
 * Первая строка: что произошло и с чем.
 *
 * Номер задачи ведёт, а не название: по нему задачу называют в разговоре,
 * и в списке из десяти строк он различает их быстрее, чем текст названия.
 */
export function headlineOf(item: NotificationItem): string {
  const { taskKey, taskTitle, boardName, noteTitle, itemText } = item.payload;
  if (taskKey) return taskTitle ? `${taskKey} · ${taskTitle}` : taskKey;
  // Напоминание о пункте — сам пункт: «Купить слона», а не название списка.
  if (item.type === NotificationCategory.NOTE_REMINDER && itemText) return itemText;
  if (noteTitle) return noteTitle;
  if (item.payload.rangeLabel) return CATEGORY_LABELS[item.type];
  return boardName ?? CATEGORY_LABELS[item.type];
}

/**
 * Вторая строка: суть события словами.
 *
 * Изменения полей сюда НЕ попадают — они показываются отдельно парами
 * «было → стало», где пара читается как пара. Свёрнутые в предложение,
 * они превращаются в строку, которую никто не дочитывает.
 */
export function summaryOf(item: NotificationItem): string {
  const { payload } = item;
  switch (item.type) {
    case NotificationCategory.ASSIGNED:
      return payload.changes?.length
        ? "Изменился состав исполнителей"
        : "Вам поручена задача";
    case NotificationCategory.UNASSIGNED:
      return "Вас сняли с задачи";
    case NotificationCategory.COMMENT:
      return payload.commentExcerpt ? `«${payload.commentExcerpt}»` : "Новый комментарий";
    case NotificationCategory.MENTIONED:
      return "Вас упомянули в обсуждении";
    case NotificationCategory.TASK_REMINDER:
      return payload.remindAt ? `Напоминание на ${reminderMomentLabel(payload.remindAt)}` : "Напоминание";
    case NotificationCategory.OVERDUE:
      return payload.dueDateLabel ? `Срок истёк ${payload.dueDateLabel}` : "Срок истёк";
    case NotificationCategory.STATUS_CHANGED:
      return "Изменился статус";
    case NotificationCategory.FIELDS_CHANGED:
      if (payload.watchLeft) return watchLeftText(payload.watchLeft);
      return item.count > 1 ? `Изменений: ${item.count}` : "Изменение в задаче";
    case NotificationCategory.ATTACHMENT:
      return payload.filename ? `Файл: ${payload.filename}` : "Прикреплён файл";
    case NotificationCategory.SUBTASK:
      return subtaskText(item);
    case NotificationCategory.ARCHIVED:
      if (payload.columnName) {
        const text = `Были архивированы все задачи столбца «${payload.columnName}»`;
        return payload.archivedCount ? `${text} (${String(payload.archivedCount)})` : text;
      }
      return payload.reason ? `Задача закрыта: ${payload.reason}` : "Задача закрыта";
    case NotificationCategory.RESTORED:
      return "Задача восстановлена из архива";
    case NotificationCategory.BOARD_INVITE:
      return "Вам открыт доступ к доске";
    case NotificationCategory.NOTE_INVITE:
      return payload.role === "EDIT"
        ? "Вас пригласили в заметку: просмотр и правка"
        : "Вас пригласили в заметку: просмотр";
    case NotificationCategory.NOTE_REMINDER: {
      const when = payload.remindAt ? reminderMomentLabel(payload.remindAt) : null;
      const where = payload.itemText && payload.noteTitle ? ` · ${payload.noteTitle}` : "";
      return when ? `Напоминание на ${when}${where}` : `Напоминание${where}`;
    }
    case NotificationCategory.VACATION_UPCOMING:
      return payload.daysLeft === undefined
        ? "Скоро отпуск"
        : `Отпуск через ${String(payload.daysLeft)} дн.${payload.rangeLabel ? `: ${payload.rangeLabel}` : ""}`;
    case NotificationCategory.VACATION_CONFLICT:
      return payload.colleagues?.length
        ? `Одновременно отдыхает: ${payload.colleagues.join(", ")}`
        : "Ваш отпуск пересёкся с отпуском коллеги";
    case NotificationCategory.VACATION_CHANGED: {
      const verb =
        payload.vacationAction === "CREATED" ? "добавлен" : payload.vacationAction === "DELETED" ? "удалён" : "изменён";
      return payload.rangeLabel ? `Ваш отпуск ${verb}: ${payload.rangeLabel}` : `Ваш отпуск ${verb}`;
    }
    case NotificationCategory.TASK_CREATED:
      return payload.statusLabel ? `Новая задача · ${payload.statusLabel}` : "Новая задача";
    case NotificationCategory.TASK_DELETED:
      return "Задача удалена";
    default:
      return CATEGORY_LABELS[item.type] ?? "Уведомление";
  }
}

const WATCH_LEFT_TARGETS = { COLUMN: "столбцом", BOARD: "доской", PROJECT: "проектом" } as const;

/** «Задача перешла в «Готово» — по наблюдению за столбцом «В работе» уведомлений больше не будет». */
export function watchLeftText(left: NonNullable<NotificationItem["payload"]["watchLeft"]>): string {
  const target = WATCH_LEFT_TARGETS[left.targetType] ?? "объектом";
  const where = left.fromName ? ` «${left.fromName}»` : "";
  return `Задача перешла в «${left.toName ?? "—"}» — по наблюдению за ${target}${where} уведомлений больше не будет`;
}

/** «10 октября в 18:00» — в часовом поясе человека, а не сервера. */
export function reminderMomentLabel(iso: string): string {
  const moment = new Date(iso);
  if (Number.isNaN(moment.getTime())) return iso;
  const date = moment.toLocaleDateString("ru-RU", { day: "numeric", month: "long" });
  const time = moment.toLocaleTimeString("ru-RU", { hour: "2-digit", minute: "2-digit" });
  return `${date} в ${time}`;
}

function subtaskText(item: NotificationItem): string {
  const { subtaskKey, subtaskTitle, subtaskState } = item.payload;
  const verb = subtaskState === "DONE" ? "выполнена" : "добавлена";
  if (!subtaskKey) return `Подзадача ${verb}`;
  return subtaskTitle
    ? `Подзадача ${verb}: ${subtaskKey} · ${subtaskTitle}`
    : `Подзадача ${verb}: ${subtaskKey}`;
}

/**
 * Кто это сделал.
 *
 * За окно схлопывания задачу могли тронуть несколько человек, и приписать
 * всё последнему было бы неправдой — поэтому список, а не одно имя.
 */
export function actorTextOf(item: NotificationItem): string | null {
  const { actors, actorName } = item.payload;
  if (actors && actors.length > 1) {
    return actors
      .map((person) => person.name)
      .filter((name): name is string => Boolean(name))
      .join(", ");
  }
  return actorName ?? null;
}

/** Адрес перехода. Пустой состав ведёт в список задач, а не в никуда. */
export function linkOf(item: NotificationItem): string {
  return item.payload.url ?? "/tasks";
}

/** Строка ленты колокольчика: одиночное уведомление или серия по задаче. */
export type NotificationEntry =
  | { kind: "single"; item: NotificationItem }
  | { kind: "group"; key: string; items: NotificationItem[] };

/**
 * Собирает уведомления одной задачи в группу. Порядок ленты сохраняется:
 * группа стоит там, где её самое свежее событие. Уведомления без задачи
 * (доступ к доске) не группируются.
 */
export function groupByTask(items: NotificationItem[]): NotificationEntry[] {
  const entries: NotificationEntry[] = [];
  const groups = new Map<string, NotificationItem[]>();

  for (const item of items) {
    if (item.taskKey === null) {
      entries.push({ kind: "single", item });
      continue;
    }
    // Номер задачи уникален в проекте, а не во всей системе.
    const key = `${item.projectId ?? ""}:${item.taskKey}`;
    const group = groups.get(key);
    if (group) {
      group.push(item);
    } else {
      const fresh = [item];
      groups.set(key, fresh);
      entries.push({ kind: "group", key, items: fresh });
    }
  }

  return entries.map((entry) =>
    entry.kind === "group" && entry.items.length === 1
      ? { kind: "single", item: entry.items[0] as NotificationItem }
      : entry,
  );
}
